import { lerpColor } from '../../color';
import { calmAt, calmCenter, catenary, grain, rgba, vignette, type Painter } from '../../art/paint';
import { hsl, type World } from '../../world';

const TAU = Math.PI * 2;
const RAYS = 12;

interface Tone {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly carve: number;
  readonly block: number;
  readonly dark: number;
  readonly wood: number;
  readonly tints: readonly number[];
}

interface Plan {
  readonly portrait: boolean;
  readonly sc: number;
  readonly sunX: number;
  readonly sunY: number;
  readonly sunR: number;
  readonly reach: number;
  readonly ridge: number;
  readonly peak: number;
  readonly ground: number;
  readonly cactusH: number;
  readonly bs: number;
}

type Col = readonly [number, number, number, number];

function tone(look: World): Tone {
  const pal = look.palette;
  return {
    bg: pal.bg,
    far: hsl(look.hue, 32, 16),
    ceil: look.ceil,
    ink: pal.ink,
    carve: pal.path,
    block: lerpColor(pal.bg, pal.ink, 0.45),
    dark: lerpColor(pal.bg, pal.ink, 0.35),
    wood: lerpColor(pal.bg, look.ceil, 0.4),
    tints: [
      lerpColor(look.ceil, pal.path, 0.35),
      lerpColor(look.ceil, pal.held, 0.3),
      lerpColor(look.ceil, pal.spot, 0.3),
      lerpColor(look.ceil, pal.incoming.circle, 0.3),
    ],
  };
}

function plan(p: Painter): Plan {
  const u = p.unit;
  if (p.w / p.h < 0.8) {
    const sunR = Math.max(24, u * 0.14);
    const reach = 1.6;
    return {
      portrait: true,
      sc: 0.6,
      sunR,
      reach,
      sunX: Math.max(sunR * reach + 8, p.w * 0.27),
      sunY: Math.max(p.h * 0.26, p.h * 0.2 + sunR * reach + 4),
      ridge: p.h * 0.64,
      peak: p.h * 0.07,
      ground: p.h * 0.8,
      cactusH: p.h * 0.225,
      bs: Math.min(30, Math.max(16, u * 0.1)),
    };
  }
  const sc = Math.min(1, p.w / p.h / 1.25);
  const sunR = Math.max(34, u * 0.13) * sc;
  const reach = 1.75;
  return {
    portrait: false,
    sc,
    sunR,
    reach,
    sunX: Math.max(sunR * reach + 8, Math.min(p.w * 0.15, p.cx - u * 1.1)),
    sunY: Math.max(p.h * 0.35, p.h * 0.2 + sunR * reach + 4),
    ridge: p.h * 0.66,
    peak: p.h * 0.14,
    ground: p.h * 0.82,
    cactusH: Math.min(p.h * 0.375, u * 0.85) * sc,
    bs: Math.min(34, Math.max(18, u * 0.085)),
  };
}

function calmHalf(p: Painter, y: number, keep: number): number {
  const dy = (y - p.cy + 0.08 * p.unit) / (keep * p.unit);
  return dy * dy >= 1 ? 0 : keep * p.unit * 1.08 * Math.sqrt(1 - dy * dy);
}

function carveSegs(p: Painter, segs: readonly number[], keep: number, alpha: number, faint: number, width: number, color: number): void {
  const { ctx } = p;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  for (const calm of [false, true]) {
    if (calm && faint <= 0) continue;
    ctx.beginPath();
    for (let i = 0; i + 3 < segs.length; i += 4) {
      const x0 = segs[i]!;
      const y0 = segs[i + 1]!;
      const x1 = segs[i + 2]!;
      const y1 = segs[i + 3]!;
      if (calmAt(p, (x0 + x1) / 2, (y0 + y1) / 2, keep) !== calm) continue;
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
    }
    ctx.strokeStyle = rgba(color, calm ? faint : alpha);
    ctx.stroke();
  }
}

function sky(p: Painter, t: Tone, pl: Plan): void {
  const { ctx } = p;
  const g = ctx.createLinearGradient(0, 0, 0, pl.ground);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.4)));
  g.addColorStop(0.5, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.6)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  const top = pl.ridge - pl.peak;
  const n = Math.round((p.w * top) / 6000);
  const segs: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * top;
    const len = p.rng.range(0.025, 0.07) * p.unit * 0.5;
    const a = p.rng.range(-0.1, 0.1);
    if (calmAt(p, x, y, 1) || Math.hypot(x - pl.sunX, y - pl.sunY) < pl.sunR * (pl.reach + 0.3)) continue;
    segs.push(x - Math.cos(a) * len, y - Math.sin(a) * len, x + Math.cos(a) * len, y + Math.sin(a) * len);
  }
  carveSegs(p, segs, 1, 0.1, 0, 1.4, t.carve);
}

function sun(p: Painter, t: Tone, pl: Plan): void {
  const { ctx } = p;
  const { sunX: x, sunY: y, sunR: r, reach } = pl;
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const rr = r * (reach + 0.4 + i * 0.32);
    const a0 = p.rng.range(0, TAU);
    const span = p.rng.range(0.5, 1.3);
    const am = a0 + span / 2;
    const my = y + Math.sin(am) * rr;
    if (calmAt(p, x + Math.cos(am) * rr, my, 1) || my < p.h * 0.2) continue;
    ctx.moveTo(x + Math.cos(a0) * rr, y + Math.sin(a0) * rr);
    ctx.arc(x, y, rr, a0, a0 + span);
  }
  ctx.lineWidth = Math.max(1, r * 0.04);
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(t.carve, 0.08);
  ctx.stroke();
  const rot = p.rng.range(0, TAU / RAYS);
  const half = (TAU / RAYS) * 0.27;
  const inner = r * 1.13;
  ctx.beginPath();
  for (let i = 0; i < RAYS; i++) {
    const a = rot + (i / RAYS) * TAU;
    const out = r * (i % 2 === 0 ? reach : reach * 0.8);
    ctx.moveTo(x + Math.cos(a - half) * inner, y + Math.sin(a - half) * inner);
    ctx.lineTo(x + Math.cos(a) * out, y + Math.sin(a) * out);
    ctx.lineTo(x + Math.cos(a + half) * inner, y + Math.sin(a + half) * inner);
    ctx.closePath();
  }
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ceil, 0.6));
  ctx.fill();
  ctx.lineWidth = Math.max(1, r * 0.025);
  ctx.strokeStyle = rgba(t.carve, 0.14);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = rgba(t.ceil);
  ctx.fill();
  ctx.lineWidth = Math.max(1.2, r * 0.05);
  ctx.strokeStyle = rgba(t.carve, 0.35);
  ctx.stroke();
  ctx.beginPath();
  for (const k of [0.3, 0.55, 0.8]) {
    const a0 = p.rng.range(0, TAU);
    ctx.moveTo(x + Math.cos(a0) * r * k, y + Math.sin(a0) * r * k);
    ctx.arc(x, y, r * k, a0, a0 + TAU * 0.78);
  }
  ctx.lineWidth = Math.max(1, r * 0.035);
  ctx.strokeStyle = rgba(t.carve, 0.22);
  ctx.stroke();
}

function serra(p: Painter, t: Tone, pl: Plan): void {
  const { ctx } = p;
  const ph1 = p.rng.range(0, TAU);
  const ph2 = p.rng.range(0, TAU);
  const at = (x: number, k: number): number => {
    const f = (0.55 + 0.45 * Math.sin((x / p.w) * TAU * 1.2 + ph1)) * (0.7 + 0.3 * Math.sin((x / p.w) * TAU * 3.3 + ph2));
    return pl.ridge - pl.peak * f * (1 - 0.25 * k) + k * pl.peak * 0.2;
  };
  const step = 6;
  ctx.beginPath();
  ctx.moveTo(-10, pl.ground + 10);
  for (let x = -10; x <= p.w + 10; x += step) ctx.lineTo(x, at(x, 0));
  ctx.lineTo(p.w + 10, pl.ground + 10);
  ctx.closePath();
  ctx.fillStyle = rgba(t.far);
  ctx.fill();
  for (let k = 0; k < 3; k++) {
    const segs: number[] = [];
    for (let x = -10; x <= p.w + 10; x += step) {
      if (k > 0 && p.rng.chance(0.22)) continue;
      segs.push(x, at(x, k), x + step, at(x + step, k));
    }
    carveSegs(p, segs, 0.95, k === 0 ? 0.26 : 0.13, 0.07, k === 0 ? 1.6 : 1.1, t.carve);
  }
}

function ground(p: Painter, t: Tone, pl: Plan): void {
  const { ctx } = p;
  const ph = p.rng.range(0, TAU);
  const at = (x: number): number => pl.ground + Math.sin((x / p.w) * TAU * 2 + ph) * p.h * 0.006;
  const edge: number[] = [];
  ctx.beginPath();
  ctx.moveTo(-10, p.h + 10);
  for (let x = -10; x <= p.w + 10; x += 8) {
    ctx.lineTo(x, at(x));
    edge.push(x, at(x), x + 8, at(x + 8));
  }
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.closePath();
  ctx.fillStyle = rgba(t.dark);
  ctx.fill();
  carveSegs(p, edge, 0.95, 0.32, 0.1, 1.6, t.carve);
  const segs: number[] = [];
  const n = Math.round(p.w / 50);
  for (let i = 0; i < n; i++) {
    let x = p.rng.float() * p.w;
    let y = p.rng.range(pl.ground + 6, p.h - 4);
    let a = p.rng.range(-0.5, 0.5) + (p.rng.chance(0.5) ? 0 : Math.PI);
    const steps = p.rng.int(3, 7);
    for (let s = 0; s < steps; s++) {
      const len = p.rng.range(0.02, 0.05) * p.unit;
      a += p.rng.range(-0.6, 0.6);
      const nx = x + Math.cos(a) * len;
      const ny = Math.min(p.h, Math.max(pl.ground + 4, y + Math.sin(a) * len * 0.6));
      segs.push(x, y, nx, ny);
      if (p.rng.chance(0.3)) {
        const b = a + (p.rng.chance(0.5) ? 1 : -1) * p.rng.range(0.6, 1.1);
        segs.push(nx, ny, nx + Math.cos(b) * len * 0.7, ny + Math.sin(b) * len * 0.42);
      }
      x = nx;
      y = ny;
    }
  }
  carveSegs(p, segs, 0.9, 0.2, 0.06, 1.2, t.carve);
}

function column(ctx: CanvasRenderingContext2D, x: number, top: number, w: number, bottom: number): void {
  ctx.moveTo(x - w / 2, bottom);
  ctx.lineTo(x - w / 2, top + w / 2);
  ctx.arc(x, top + w / 2, w / 2, Math.PI, 0);
  ctx.lineTo(x + w / 2, bottom);
  ctx.closePath();
}

function carved(p: Painter, t: Tone, cols: readonly Col[], stubs: readonly Col[], lw: number, spines: boolean): void {
  const { ctx } = p;
  const shape = (): void => {
    ctx.beginPath();
    for (const [x, top, w, bottom] of cols) column(ctx, x, top, w, bottom);
    for (const [x, y, len, w] of stubs) ctx.roundRect(x, y - w / 2, len, w, w / 2);
  };
  shape();
  ctx.lineJoin = 'round';
  ctx.lineWidth = lw * 2;
  ctx.strokeStyle = rgba(t.carve, 0.5);
  ctx.stroke();
  ctx.fillStyle = rgba(t.block);
  ctx.fill();
  ctx.save();
  shape();
  ctx.clip();
  ctx.beginPath();
  for (const [x, top, w, bottom] of cols) {
    for (const o of spines ? [-0.26, 0.26] : [0]) {
      ctx.moveTo(x + o * w, top + w * 0.5);
      ctx.lineTo(x + o * w, bottom);
    }
  }
  ctx.lineWidth = Math.max(1, lw * 0.75);
  ctx.strokeStyle = rgba(t.carve, 0.24);
  ctx.stroke();
  if (spines) {
    ctx.beginPath();
    for (const [x, top, w, bottom] of cols) {
      for (let y = top + w * 0.7; y < bottom - w * 0.2; y += w * 0.42) {
        ctx.moveTo(x - w * 0.5, y);
        ctx.lineTo(x - w * 0.36, y - w * 0.06);
        ctx.moveTo(x + w * 0.5, y);
        ctx.lineTo(x + w * 0.36, y - w * 0.06);
      }
    }
    ctx.strokeStyle = rgba(t.carve, 0.28);
    ctx.stroke();
  }
  ctx.restore();
}

function cactus(p: Painter, t: Tone, x: number, base: number, h: number, flip: boolean): void {
  const tw = h * 0.13;
  const aw = tw * 0.8;
  const s = flip ? -1 : 1;
  const arms: readonly (readonly [number, number, number])[] = [
    [x - s * h * 0.19, base - h * 0.74, base - h * 0.42],
    [x + s * h * 0.17, base - h * 0.86, base - h * 0.56],
  ];
  const cols: Col[] = [[x, base - h, tw, base]];
  const stubs: Col[] = [];
  for (const [ax, top, elbow] of arms) {
    cols.push([ax, top, aw, elbow]);
    stubs.push([Math.min(x, ax) - aw / 2, elbow, Math.abs(ax - x) + aw, aw]);
  }
  carved(p, t, cols, stubs, Math.max(1.2, tw * 0.07), true);
}

function house(p: Painter, t: Tone, x: number, base: number, w: number): void {
  const { ctx } = p;
  const h = w * 0.52;
  const top = base - h;
  const left = x - w / 2;
  const lw = Math.max(1.2, w * 0.008);
  ctx.fillStyle = rgba(t.wood);
  ctx.fillRect(left, top, w, h);
  ctx.beginPath();
  for (let i = 0; i < 14; i++) {
    const mx = left + p.rng.range(0.05, 0.85) * w;
    const my = top + p.rng.range(0.1, 0.95) * h;
    ctx.moveTo(mx, my);
    ctx.lineTo(mx + p.rng.range(0.03, 0.08) * w, my + p.rng.range(-0.01, 0.01) * w);
  }
  ctx.lineWidth = lw;
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(t.carve, 0.12);
  ctx.stroke();
  const px = left + w * 0.13;
  const py = top + h * 0.34;
  const pr = w * 0.08;
  ctx.save();
  ctx.beginPath();
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU;
    const rr = pr * p.rng.range(0.7, 1.15);
    if (i === 0) ctx.moveTo(px + Math.cos(a) * rr * 1.2, py + Math.sin(a) * rr);
    else ctx.lineTo(px + Math.cos(a) * rr * 1.2, py + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fillStyle = rgba(t.dark);
  ctx.fill();
  ctx.strokeStyle = rgba(t.carve, 0.3);
  ctx.stroke();
  ctx.clip();
  ctx.beginPath();
  for (let d = -pr * 2; d <= pr * 2; d += w * 0.03) {
    ctx.moveTo(px + d - pr, py - pr * 1.2);
    ctx.lineTo(px + d + pr, py + pr * 1.2);
    ctx.moveTo(px + d + pr, py - pr * 1.2);
    ctx.lineTo(px + d - pr, py + pr * 1.2);
  }
  ctx.lineWidth = Math.max(1, lw * 0.8);
  ctx.strokeStyle = rgba(t.carve, 0.28);
  ctx.stroke();
  ctx.restore();
  const dw = w * 0.2;
  const dh = h * 0.74;
  const dx = x - w * 0.1 - dw / 2;
  ctx.fillStyle = rgba(t.ink, 0.92);
  ctx.fillRect(dx, base - dh, dw, dh);
  const ww = w * 0.16;
  const wx = x + w * 0.24 - ww / 2;
  const wy = top + h * 0.22;
  ctx.fillRect(wx, wy, ww, ww);
  ctx.lineWidth = lw;
  ctx.strokeStyle = rgba(t.carve, 0.42);
  ctx.strokeRect(dx, base - dh, dw, dh);
  ctx.strokeRect(wx, wy, ww, ww);
  ctx.strokeRect(left, top, w, h);
  ctx.beginPath();
  ctx.moveTo(dx + dw / 2, base - dh);
  ctx.lineTo(dx + dw / 2, base);
  ctx.moveTo(wx + ww / 2, wy);
  ctx.lineTo(wx + ww / 2, wy + ww);
  ctx.moveTo(wx, wy + ww / 2);
  ctx.lineTo(wx + ww, wy + ww / 2);
  ctx.strokeStyle = rgba(t.carve, 0.24);
  ctx.stroke();
  const roof = w * 0.26;
  const ry = top - roof;
  const tracePath = (): void => {
    ctx.beginPath();
    ctx.moveTo(x - w * 0.58, top + 2);
    ctx.lineTo(x - w * 0.42, ry);
    ctx.lineTo(x + w * 0.42, ry);
    ctx.lineTo(x + w * 0.58, top + 2);
    ctx.closePath();
  };
  tracePath();
  ctx.fillStyle = rgba(t.block);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.beginPath();
  const sp = w * 0.07;
  for (let i = 0; i < 4; i++) {
    const y = ry + (i + 0.6) * (roof / 4);
    for (let tx = x - w * 0.62 + (i % 2) * sp * 0.5; tx < x + w * 0.62; tx += sp) {
      ctx.moveTo(tx + sp, y);
      ctx.arc(tx + sp / 2, y, sp / 2, 0, Math.PI);
    }
  }
  ctx.lineWidth = lw;
  ctx.strokeStyle = rgba(t.carve, 0.26);
  ctx.stroke();
  ctx.restore();
  tracePath();
  ctx.lineWidth = lw * 1.2;
  ctx.strokeStyle = rgba(t.carve, 0.5);
  ctx.stroke();
}

function post(p: Painter, t: Tone, x: number, top: number, base: number): void {
  const { ctx } = p;
  const w = Math.max(4, p.unit * 0.014);
  ctx.fillStyle = rgba(t.block);
  ctx.fillRect(x - w / 2, top, w, base - top);
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(t.carve, 0.4);
  ctx.strokeRect(x - w / 2, top, w, base - top);
}

function folheto(p: Painter, t: Tone, x: number, y: number, bs: number, rot: number, i: number): void {
  const { ctx } = p;
  const fw = bs * 0.72;
  const tint = t.tints[i % t.tints.length]!;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.fillStyle = rgba(tint, 0.94);
  ctx.fillRect(-fw / 2, bs * 0.04, fw, bs);
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(t.ink, 0.55);
  ctx.strokeRect(-fw / 2, bs * 0.04, fw, bs);
  ctx.fillStyle = rgba(t.ink, 0.78);
  ctx.fillRect(-fw * 0.36, bs * 0.36, fw * 0.72, bs * 0.5);
  const my = bs * 0.61;
  const mr = fw * 0.15;
  ctx.beginPath();
  if (i % 3 === 0) {
    ctx.arc(0, my, mr, 0, TAU);
  } else if (i % 3 === 1) {
    ctx.moveTo(-mr * 1.4, my + mr);
    ctx.lineTo(0, my - mr * 1.1);
    ctx.lineTo(mr * 1.4, my + mr);
    ctx.closePath();
  } else {
    ctx.moveTo(0, my - mr * 1.3);
    ctx.lineTo(mr, my);
    ctx.lineTo(0, my + mr * 1.3);
    ctx.lineTo(-mr, my);
    ctx.closePath();
  }
  ctx.fillStyle = rgba(tint, 0.8);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-fw * 0.32, bs * 0.15);
  ctx.lineTo(fw * 0.32, bs * 0.15);
  ctx.moveTo(-fw * 0.22, bs * 0.25);
  ctx.lineTo(fw * 0.22, bs * 0.25);
  ctx.lineWidth = Math.max(1, bs * 0.05);
  ctx.strokeStyle = rgba(t.ink, 0.6);
  ctx.stroke();
  ctx.fillStyle = rgba(t.block);
  ctx.fillRect(-bs * 0.05, -bs * 0.08, bs * 0.1, bs * 0.2);
  ctx.restore();
}

function line(p: Painter, t: Tone, x0: number, y0: number, x1: number, y1: number, sag: number, keep: number, bs: number): void {
  const { ctx } = p;
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const q = catenary(x0, y0, x1, y1, sag, i / 24);
    if (i === 0) ctx.moveTo(q.x, q.y);
    else ctx.lineTo(q.x, q.y);
  }
  ctx.lineWidth = Math.max(1, bs * 0.05);
  ctx.strokeStyle = rgba(t.carve, 0.45);
  ctx.stroke();
  const span = Math.abs(x1 - x0);
  const room = span - bs * 1.2;
  const n = Math.max(0, Math.floor(room / (bs * 0.95)));
  for (let i = 0; i < n; i++) {
    const q = catenary(x0, y0, x1, y1, sag, (bs * 0.6 + ((i + 0.5) * room) / n) / span);
    const rot = p.rng.range(-0.09, 0.09);
    if (calmAt(p, q.x, q.y + bs * 0.5, keep) || q.x < bs * 0.5 || q.x > p.w - bs * 0.5) continue;
    folheto(p, t, q.x, q.y, bs, rot, i);
  }
}

function birds(p: Painter, t: Tone, pl: Plan): void {
  const { ctx } = p;
  const s = p.unit * 0.03;
  const minX = pl.portrait ? pl.sunX + pl.sunR * pl.reach : p.cx;
  const maxY = pl.ridge - pl.peak - s * 2;
  ctx.beginPath();
  let placed = 0;
  for (let i = 0; i < 60 && placed < 4; i++) {
    const x = p.rng.range(minX, p.w - s * 2);
    const y = p.rng.range(p.h * 0.22, maxY);
    const k = p.rng.range(0.7, 1.15) * s;
    if (calmAt(p, x, y, 1.05)) continue;
    ctx.moveTo(x - k, y - k * 0.25);
    ctx.quadraticCurveTo(x - k * 0.45, y - k * 0.6, x, y);
    ctx.quadraticCurveTo(x + k * 0.45, y - k * 0.6, x + k, y - k * 0.25);
    placed++;
  }
  ctx.lineWidth = Math.max(1.2, s * 0.14);
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(t.carve, 0.4);
  ctx.stroke();
}

function stone(p: Painter, t: Tone, x: number, y: number, r: number): void {
  const { ctx } = p;
  ctx.beginPath();
  ctx.ellipse(x, y - r * 0.3, r, r * 0.55, 0, 0, TAU);
  ctx.fillStyle = rgba(t.block);
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(t.carve, 0.3);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x, y - r * 0.3, r * 0.7, r * 0.35, 0, Math.PI * 1.1, Math.PI * 1.6);
  ctx.strokeStyle = rgba(t.carve, 0.35);
  ctx.stroke();
}

function scrub(p: Painter, t: Tone, pl: Plan): void {
  const s = p.unit * 0.075 * (pl.portrait ? 0.8 : pl.sc);
  const bottom = Math.min(p.h - s * 0.5, pl.ground + (p.h - pl.ground) * 0.75);
  let clumps = 0;
  let stones = 0;
  for (let i = 0; i < 80 && (clumps < 2 || stones < 4); i++) {
    const x = p.rng.range(s, p.w - s);
    const y = p.rng.range(pl.ground + s * 0.4, bottom);
    const clump = clumps < 2 && p.rng.chance(0.4);
    const r = s * p.rng.range(0.25, 0.45);
    if (calmAt(p, x, y - s * 0.5, 0.95)) continue;
    if (clump) {
      const cols: Col[] = [
        [x, y - s, s * 0.22, y],
        [x - s * 0.3, y - s * 0.65, s * 0.18, y],
        [x + s * 0.32, y - s * 0.75, s * 0.18, y],
      ];
      carved(p, t, cols, [], Math.max(1, s * 0.03), false);
      clumps++;
    } else if (stones < 4) {
      stone(p, t, x, y, r);
      stones++;
    }
  }
}

function viola(p: Painter, t: Tone, x: number, base: number, size: number, lean: number): void {
  const { ctx } = p;
  const b = size * 0.17;
  const a = size * 0.13;
  const uy = -2 * b - a * 0.55;
  const lw = Math.max(1, size * 0.012);
  ctx.save();
  ctx.translate(x, base);
  ctx.rotate(lean);
  ctx.fillStyle = rgba(t.block);
  ctx.fillRect(-size * 0.028, -size * 0.88, size * 0.056, size * 0.88 + uy - a * 0.5);
  ctx.fillRect(-size * 0.04, -size, size * 0.08, size * 0.12);
  ctx.lineWidth = lw;
  ctx.strokeStyle = rgba(t.carve, 0.38);
  ctx.strokeRect(-size * 0.028, -size * 0.88, size * 0.056, size * 0.88 + uy - a * 0.5);
  ctx.strokeRect(-size * 0.04, -size, size * 0.08, size * 0.12);
  ctx.beginPath();
  ctx.arc(0, -b, b, 0, TAU);
  ctx.moveTo(a, uy);
  ctx.arc(0, uy, a, 0, TAU);
  ctx.lineWidth = lw * 2;
  ctx.strokeStyle = rgba(t.carve, 0.5);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.carve, 0.2));
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, -b * 1.55, a * 0.32, 0, TAU);
  ctx.fillStyle = rgba(t.ink, 0.9);
  ctx.fill();
  ctx.fillStyle = rgba(t.block);
  ctx.fillRect(-b * 0.35, -b * 0.55, b * 0.7, b * 0.12);
  ctx.beginPath();
  for (const dx of [-0.012, 0, 0.012]) {
    ctx.moveTo(dx * size, -b * 0.5);
    ctx.lineTo(dx * size * 0.6, -size * 0.88);
  }
  ctx.moveTo(b * 0.78, -b);
  ctx.arc(0, -b, b * 0.78, 0, Math.PI * 0.9);
  ctx.lineWidth = Math.max(0.8, lw * 0.6);
  ctx.strokeStyle = rgba(t.carve, 0.3);
  ctx.stroke();
  ctx.restore();
}

function spiral(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  for (let s = 0; s <= 30; s++) {
    const q = s / 30;
    const a = q * TAU * 1.6;
    const rr = r * (0.15 + 0.85 * q);
    if (s === 0) ctx.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
}

function clouds(p: Painter, t: Tone, pl: Plan): void {
  const { ctx } = p;
  const r = p.unit * 0.05;
  ctx.beginPath();
  let placed = 0;
  for (let i = 0; i < 50 && placed < 2; i++) {
    const x = p.rng.range(r * 3, p.w - r * 3);
    const y = p.rng.range(p.h * 0.22 + r, pl.ridge - pl.peak - r * 1.5);
    if (calmAt(p, x, y, 1.05) || Math.hypot(x - pl.sunX, y - pl.sunY) < pl.sunR * (pl.reach + 1.2)) continue;
    for (let j = -1; j <= 1; j++) spiral(ctx, x + j * r * 1.5, y - (j === 0 ? r * 0.4 : 0), r * (j === 0 ? 1 : 0.75));
    ctx.moveTo(x - r * 2.4, y + r * 0.7);
    ctx.lineTo(x + r * 2.4, y + r * 0.7);
    placed++;
  }
  ctx.lineWidth = Math.max(1, r * 0.08);
  ctx.strokeStyle = rgba(t.carve, 0.24);
  ctx.stroke();
}

export function paint(p: Painter, look: World): void {
  const t = tone(look);
  const pl = plan(p);
  sky(p, t, pl);
  sun(p, t, pl);
  serra(p, t, pl);
  ground(p, t, pl);
  const h = pl.cactusH;
  const margin = h * 0.242 + 6;
  let fiddle: readonly [number, number, number, number];
  if (pl.portrait) {
    const lx = margin;
    const rx = p.w - margin;
    cactus(p, t, lx, pl.ground + 2, h, false);
    cactus(p, t, rx, pl.ground + 2, h, true);
    const ys = Math.min(pl.ground - pl.bs * 1.5, Math.max(p.h * 0.73, p.cy + 0.8 * p.unit));
    line(p, t, lx, ys, rx, ys, p.unit * 0.06, 0.85, pl.bs);
    fiddle = [rx - h * 0.3, pl.ground + 2, h * 0.55, 0.22];
  } else {
    const lx = Math.max(p.w * 0.055, margin);
    const w = Math.min(p.unit * 0.55, p.w * 0.2) * pl.sc;
    const base = pl.ground + 3;
    const hx = Math.min(p.w - w * 0.6 - 6, Math.max(p.w * 0.86, p.cx + calmHalf(p, base - w * 0.78, 1) + w * 0.6));
    cactus(p, t, hx + w * 0.3, base, h * 0.75, true);
    house(p, t, hx, base, w);
    cactus(p, t, lx, pl.ground + 2, h, false);
    const y0 = pl.ground - h * 0.3;
    const y1 = pl.ground - h * 0.26;
    const xp = p.cx - calmHalf(p, y1, 1) - p.unit * 0.03;
    if (xp - lx > pl.bs * 2.5) {
      post(p, t, xp, y1 - 4, pl.ground + 3);
      line(p, t, lx, y0, xp, y1, p.unit * 0.05, 0.95, pl.bs);
    }
    fiddle = [hx + w * 0.36, base, w * 0.5, 0.18];
  }
  if (p.level >= 2) birds(p, t, pl);
  if (p.level >= 3) scrub(p, t, pl);
  if (p.level >= 4) viola(p, t, ...fiddle);
  if (p.level >= 5) clouds(p, t, pl);
  calmCenter(p, t.bg, 0.6, 1.1);
  grain(p, 0.08, true);
  vignette(p, t.ink, 0.45);
}
